'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { TEMPLATES, getTemplatePath, getScaledTemplateInfo } from '@/lib/templates';
import { computeUnionCutline } from '@/lib/pathUnion';

const ITEM_TYPES = [
  { value: 'sticker', label: 'Sticker / Patch' },
  { value: 'keychain', label: 'Leatherette Keychain' },
  { value: 'bookmark', label: 'Leatherette Bookmark' },
  { value: 'dogtag', label: 'Dog Tag' },
  { value: 'businesscard', label: 'Business Card' },
];

const TEXT_FONTS = [
  { value: 'Inter', label: 'Inter', style: 'clean sans-serif' },
  { value: 'Playfair Display', label: 'Playfair Display', style: 'elegant serif' },
  { value: 'Permanent Marker', label: 'Permanent Marker', style: 'hand-drawn' },
  { value: 'Black Ops One', label: 'Black Ops One', style: 'military stencil' },
  { value: 'Oswald', label: 'Oswald', style: 'condensed' },
  { value: 'Caveat', label: 'Caveat', style: 'handwriting' },
];

const MATERIALS = [
  { id: 'leather', label: 'Leather', className: 'material-leather', swatchBg: 'linear-gradient(145deg, #5c3d2e, #3d2518)' },
  { id: 'black-leatherette', label: 'Black Leatherette', className: 'material-black-leatherette', swatchBg: 'linear-gradient(145deg, #1a1a1a, #0d0d0d)' },
  { id: 'wood', label: 'Wood', className: 'material-wood', swatchBg: 'linear-gradient(145deg, #b8824a, #7a5229)' },
  { id: 'acrylic', label: 'Acrylic', className: 'material-acrylic', swatchBg: 'linear-gradient(135deg, #1a1a2e, #0f3460)' },
  { id: 'steel', label: 'Steel', className: 'material-steel', swatchBg: 'linear-gradient(145deg, #4a4a5a, #5a5a6a)' },
];

const CUTLINE_TYPES = [
  { value: 'contour', label: 'Auto Contour' },
  { value: 'circle', label: 'Circle' },
  { value: 'rectangle', label: 'Rectangle' },
  { value: 'none', label: 'No Cutline' },
];

const ENGRAVE_COLORS = [
  { value: '#000000', label: 'Black', swatch: '#000000' },
  { value: '#C0C0C0', label: 'Silver', swatch: '#C0C0C0' },
  { value: '#FFFFFF', label: 'White', swatch: '#FFFFFF' },
  { value: '#D4A574', label: 'Gold', swatch: '#D4A574' },
  { value: '#8B4513', label: 'Brown', swatch: '#8B4513' },
];

// Best-contrast engrave color per material
const MATERIAL_ENGRAVE_MAP = {
  'leather': '#000000',
  'black-leatherette': '#C0C0C0',
  'wood': '#000000',
  'acrylic': '#FFFFFF',
  'steel': '#000000',
};

const SNAP_THRESHOLD = 6; // px distance to snap

// Google Fonts URL for embedding in exports
const GOOGLE_FONTS_URL = 'https://fonts.googleapis.com/css2?family=Black+Ops+One&family=Caveat:wght@400;700&family=Inter:wght@400;600;700&family=Oswald:wght@400;600;700&family=Permanent+Marker&family=Playfair+Display:wght@400;700&display=swap';

// Cache for embedded font CSS with base64 data URIs
let _embeddedFontCSSCache = null;

/**
 * Fetches Google Fonts CSS, downloads all woff2 files, converts to base64,
 * and returns a self-contained @font-face CSS string for SVG embedding.
 */
async function getEmbeddedFontCSS() {
  if (_embeddedFontCSSCache) return _embeddedFontCSSCache;
  try {
    // Fetch CSS with woff2-capable user agent so Google returns woff2 URLs
    const res = await fetch(GOOGLE_FONTS_URL, {
      headers: { 'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 Chrome/120' }
    });
    let css = await res.text();
    // Find all url() references
    const urlRegex = /url\(([^)]+)\)/g;
    const urls = new Set();
    let match;
    while ((match = urlRegex.exec(css)) !== null) {
      urls.add(match[1]);
    }
    // Download each font file and convert to base64 data URI
    for (const fontUrl of urls) {
      try {
        const fontRes = await fetch(fontUrl);
        const blob = await fontRes.blob();
        const dataUri = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        css = css.replaceAll(fontUrl, dataUri);
      } catch { /* skip fonts that fail to download */ }
    }
    _embeddedFontCSSCache = css;
    return css;
  } catch (e) {
    console.warn('Failed to embed fonts:', e);
    return '';
  }
}

export default function Home() {
  // Global Setup State
  const [itemType, setItemType] = useState('sticker');
  const [templateShape, setTemplateShape] = useState('circle');
  const [stickerShape, setStickerShape] = useState('none'); // for sticker/patch die-cut
  const [material, setMaterial] = useState('leather');

  // Shape stretch (temporary per-project override)
  const [shapeStretchX, setShapeStretchX] = useState(1);
  const [shapeStretchY, setShapeStretchY] = useState(1);
  const [editingShape, setEditingShape] = useState(false);

  // Keychain link bridge width (viewBox units, default 80 in 300-wide viewBox)
  const [linkWidth, setLinkWidth] = useState(80);

  // Physical output size — either field is editable, the other derives from aspect ratio
  const [outputWidthInches, setOutputWidthInches] = useState(2);
  const [outputHeightInches, setOutputHeightInches] = useState(4);
  const [sizeDriver, setSizeDriver] = useState('w'); // 'w' or 'h' — which field was last manually set
  const [exportDpi] = useState(300);

  // Compute final physical dimensions based on which field drives
  const getPhysicalSize = () => {
    let cw, ch;
    if (customShape) {
      cw = Math.round(customShape.width * shapeStretchX);
      ch = Math.round(customShape.height * shapeStretchY);
    } else if (itemType === 'sticker') {
      const st = TEMPLATES.sticker?.[stickerShape];
      cw = stickerShape !== 'none' && st ? Math.round(st.width * shapeStretchX) : 500;
      ch = stickerShape !== 'none' && st ? Math.round(st.height * shapeStretchY) : 500;
    } else {
      const t = TEMPLATES[itemType]?.[templateShape];
      cw = t ? Math.round(t.width * shapeStretchX) : 500;
      ch = t ? Math.round(t.height * shapeStretchY) : 500;
    }
    const aspect = ch / cw;
    if (sizeDriver === 'w') {
      return { w: outputWidthInches, h: +(outputWidthInches * aspect).toFixed(3) };
    } else {
      return { w: +(outputHeightInches / aspect).toFixed(3), h: outputHeightInches };
    }
  };
  
  // Elements State
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  
  // Global Cutline State (used mainly for Sticker mode)
  const [globalCutlineType, setGlobalCutlineType] = useState('contour');
  const [globalOffsetMm, setGlobalOffsetMm] = useState(2);

  // Canvas View State
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  
  // Interaction Refs
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });
  const previewCardRef = useRef(null);
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  
  // Drag/Transform Element Refs
  const isTransforming = useRef(null);
  const isStretchingShape = useRef(null); // { side: 'left'|'right'|'top'|'bottom', startX, startY, startStretchX, startStretchY }
  
  // Snap guides (shown during drag)
  const [snapGuides, setSnapGuides] = useState([]); // [{ axis: 'x'|'y', pos }]
  
  // Clipboard & Context Menu
  const [copiedElement, setCopiedElement] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);

  // Saved Designs & Edit
  const [savedDesigns, setSavedDesigns] = useState([]);
  const abortControllers = useRef({});

  // Text element editing
  const [editingTextId, setEditingTextId] = useState(null);

  // Presets
  const [presets, setPresets] = useState([]);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [presetSearch, setPresetSearch] = useState('');
  const [presetDeleteConfirm, setPresetDeleteConfirm] = useState(null); // id of preset pending delete
  const [presetSaving, setPresetSaving] = useState(false);
  const [presetCategory, setPresetCategory] = useState('all'); // 'all' or an itemType value
  const [showUnitCalc, setShowUnitCalc] = useState(false);
  const [unitMm, setUnitMm] = useState('');
  const [unitInches, setUnitInches] = useState('');

  // State outlines & Vframe borders
  const [statesList, setStatesList] = useState([]);
  const [vframeIndex, setVframeIndex] = useState(null); // { P1: { label, frames: [{id, svgContent}] }, ... }
  const [vframePack, setVframePack] = useState('P1');
  const [selectedState, setSelectedState] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [customShape, setCustomShape] = useState(null); // { path, viewBox, width, height, source: 'state'|'vframe', id }

  const fetchPresets = () => {
    fetch('/api/presets').then(r => r.json()).then(data => {
      if (Array.isArray(data)) setPresets(data);
    }).catch(() => {});
  };

  useEffect(() => {
    fetch('/api/designs').then(r => r.json()).then(data => {
      if (Array.isArray(data)) setSavedDesigns(data);
    }).catch(() => {});
    // Load states list
    fetch('/api/shapes/states').then(r => r.json()).then(data => {
      if (data.states) setStatesList(data.states);
    }).catch(() => {});
    // Load vframe index
    fetch('/api/shapes/vframes').then(r => r.json()).then(data => {
      setVframeIndex(data);
    }).catch(() => {});
    // Load presets
    fetchPresets();
  }, []);

  // -- Preset Functions --
  const saveAsPreset = async () => {
    const name = prompt('Preset name:');
    if (!name?.trim()) return;
    setPresetSaving(true);
    try {
      // Strip non-serializable data from elements (File objects, blob URLs)
      const serializableElements = elements.map(el => {
        const { file, ...rest } = el;
        return rest;
      });
      const designData = {
        itemType,
        templateShape,
        stickerShape,
        material,
        shapeStretchX,
        shapeStretchY,
        linkWidth,
        outputWidthInches,
        outputHeightInches,
        sizeDriver,
        globalCutlineType,
        globalOffsetMm,
        customShape,
        elements: serializableElements,
      };
      const category = ITEM_TYPES.find(t => t.value === itemType)?.label || itemType;
      await fetch('/api/presets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), category, data: designData }),
      });
      fetchPresets();
    } catch (e) {
      console.error('Failed to save preset:', e);
    }
    setPresetSaving(false);
  };

  const loadPreset = (preset) => {
    const d = preset.data;
    if (!d) return;
    setItemType(d.itemType || 'sticker');
    setTemplateShape(d.templateShape || 'circle');
    setStickerShape(d.stickerShape || 'none');
    setMaterial(d.material || 'leather');
    setShapeStretchX(d.shapeStretchX || 1);
    setShapeStretchY(d.shapeStretchY || 1);
    setLinkWidth(d.linkWidth || 80);
    setOutputWidthInches(d.outputWidthInches || 2);
    setOutputHeightInches(d.outputHeightInches || 4);
    setSizeDriver(d.sizeDriver || 'w');
    setGlobalCutlineType(d.globalCutlineType || 'contour');
    setGlobalOffsetMm(d.globalOffsetMm || 2);
    setCustomShape(d.customShape || null);
    setElements(d.elements || []);
    setSelectedId(null);
    setEditingTextId(null);
    setShowPresetModal(false);
  };

  const deletePreset = async (id) => {
    try {
      await fetch(`/api/presets/${id}`, { method: 'DELETE' });
      fetchPresets();
      setPresetDeleteConfirm(null);
    } catch (e) {
      console.error('Failed to delete preset:', e);
    }
  };

  // -- API Processing --
  const processElement = async (el) => {
    if (!el.file) return;

    if (abortControllers.current[el.id]) {
      abortControllers.current[el.id].abort();
    }
    const controller = new AbortController();
    abortControllers.current[el.id] = controller;

    setElements(prev => prev.map(e => e.id === el.id ? { ...e, processing: true, error: null } : e));

    try {
      const formData = new FormData();
      formData.append('file', el.file);
      formData.append('removeBackground', String(el.options.removeBackground));
      formData.append('threshold', String(el.options.threshold));
      formData.append('invertColors', String(el.options.invertColors));
      formData.append('engraveColor', el.options.engraveColor);
      
      // Cutline settings (only meaningful for sticker, but we pass them anyway)
      formData.append('offsetMm', String(globalOffsetMm));
      formData.append('cutlineType', itemType === 'sticker' ? globalCutlineType : 'none');
      formData.append('saveToDb', 'false');

      const res = await fetch('/api/process-logo', {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });

      if (!res.ok) throw new Error((await res.json()).error || 'Processing failed');
      const data = await res.json();
      
      setElements(prev => prev.map(e => {
        if (e.id === el.id) {
          // If this is the first time it's processed, center and size it
          let newWidth = e.width;
          let newHeight = e.height;
          if (e.width === 0) {
            // fit inside 200x200
            const aspect = data.width / data.height;
            if (aspect > 1) {
              newWidth = 200; newHeight = 200 / aspect;
            } else {
              newHeight = 200; newWidth = 200 * aspect;
            }
          }
          return { ...e, result: data, processing: false, width: newWidth, height: newHeight };
        }
        return e;
      }));
    } catch (err) {
      if (err.name !== 'AbortError') {
        setElements(prev => prev.map(e => e.id === el.id ? { ...e, processing: false, error: err.message } : e));
      }
    }
  };

  const handleFile = useCallback((selectedFile) => {
    if (selectedFile && selectedFile.type.startsWith('image/')) {
      // Get canvas center based on current template
      const t = TEMPLATES[itemType]?.[templateShape];
      const cw = itemType === 'sticker' ? 500 : (t ? Math.round(t.width * shapeStretchX) : 500);
      const ch = itemType === 'sticker' ? 500 : (t ? Math.round(t.height * shapeStretchY) : 500);
      const newEl = {
        id: Date.now().toString(),
        file: selectedFile,
        result: null,
        processing: false,
        error: null,
        x: cw / 2,
        y: ch / 2,
        width: 0,
        height: 0,
        rotation: 0,
        options: {
          removeBackground: false,
          threshold: 128,
          invertColors: false,
          engraveColor: '#000000',
        }
      };
      setElements(prev => {
        if (itemType === 'sticker') return [newEl];
        return [...prev, newEl];
      });
      setSelectedId(newEl.id);
      processElement(newEl);
      setZoom(1);
      setPanOffset({ x: 0, y: 0 });
    }
  }, [itemType, templateShape, shapeStretchX, shapeStretchY, linkWidth, globalCutlineType, globalOffsetMm]);

  const updateElementOption = (id, key, value) => {
    setElements(prev => {
      const updated = prev.map(e => e.id === id ? { ...e, options: { ...e.options, [key]: value } } : e);
      const changedEl = updated.find(e => e.id === id);
      // Debounce process trigger
      setTimeout(() => processElement(changedEl), 400);
      return updated;
    });
  };

  const removeElement = (id) => {
    setElements(prev => prev.filter(e => e.id !== id));
    if (selectedId === id) { setSelectedId(null); setEditingTextId(null); }
  };

  // -- Text Elements --
  const addTextElement = () => {
    const t = TEMPLATES[itemType]?.[templateShape];
    const cw = itemType === 'sticker' ? (TEMPLATES.sticker?.[stickerShape]?.width || 500) : (t ? Math.round(t.width * shapeStretchX) : 500);
    const ch = itemType === 'sticker' ? (TEMPLATES.sticker?.[stickerShape]?.height || 500) : (t ? Math.round(t.height * shapeStretchY) : 500);
    const bestColor = MATERIAL_ENGRAVE_MAP[material] || '#000000';
    const newEl = {
      id: Date.now().toString(),
      type: 'text',
      text: 'New Text',
      fontFamily: 'Black Ops One',
      fontSize: 32,
      fontWeight: 'normal',
      textAlign: 'center',
      textDecoration: 'none',
      color: bestColor,
      x: cw / 2,
      y: ch / 2,
      width: 200,
      height: 50,
      rotation: 0,
    };
    setElements(prev => [...prev, newEl]);
    setSelectedId(newEl.id);
    setEditingTextId(newEl.id);
  };

  const updateTextProp = (id, key, value) => {
    setElements(prev => prev.map(e => e.id === id ? { ...e, [key]: value } : e));
  };

  // Load a state outline as the custom shape
  const loadStateShape = async (stateName) => {
    setSelectedState(stateName);
    if (!stateName) { setCustomShape(null); return; }
    try {
      const res = await fetch(`/api/shapes/states?state=${encodeURIComponent(stateName)}`);
      const data = await res.json();
      if (data.paths?.length) {
        setCustomShape({
          path: data.paths.join(' '),
          viewBox: data.viewBox,
          width: data.width,
          height: data.height,
          source: 'state',
          id: stateName,
        });
        setStickerShape('none'); // clear patch shape since custom overrides
        setShapeStretchX(1); setShapeStretchY(1);
      }
    } catch (e) { console.error('Failed to load state shape', e); }
  };

  // Add a vframe as a draggable element on the canvas
  const addVframeElement = (frame) => {
    if (!frame?.svgContent) return;

    const svgContent = frame.svgContent;

    // Parse viewBox for aspect ratio
    const vbMatch = svgContent.match(/viewBox="([^"]+)"/);
    const vbParts = vbMatch ? vbMatch[1].split(/\s+/).map(Number) : [0, 0, 400, 400];
    const svgW = vbParts[2] || 400;
    const svgH = vbParts[3] || 400;

    // Determine canvas center for placement
    const previewW = (() => {
      if (customShape) return Math.round(customShape.width * shapeStretchX);
      if (itemType === 'sticker') return stickerShape !== 'none' ? Math.round((TEMPLATES.sticker?.[stickerShape]?.width || 500) * shapeStretchX) : 500;
      return Math.round((TEMPLATES[itemType]?.[templateShape]?.width || 500) * shapeStretchX);
    })();
    const previewH = (() => {
      if (customShape) return Math.round(customShape.height * shapeStretchY);
      if (itemType === 'sticker') return stickerShape !== 'none' ? Math.round((TEMPLATES.sticker?.[stickerShape]?.height || 500) * shapeStretchY) : 500;
      return Math.round((TEMPLATES[itemType]?.[templateShape]?.height || 500) * shapeStretchY);
    })();

    // Size the vframe to fill most of the canvas
    const fitSize = Math.min(previewW, previewH) * 0.85;
    const aspect = svgH / svgW;
    const elW = fitSize;
    const elH = fitSize * aspect;

    const newEl = {
      id: Date.now().toString(),
      type: 'vframe',
      svgContent,
      x: previewW / 2,
      y: previewH / 2,
      width: elW,
      height: elH,
      rotation: 0,
      frameId: frame.id,
    };
    setElements(prev => [...prev, newEl]);
    setSelectedId(newEl.id);
  };

  // Keyboard shortcuts: Delete, Ctrl+C, Ctrl+V
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.target.contentEditable === 'true') return; // Skip when editing text in-canvas
      if (editingTextId) return; // Skip when text edit mode is active

      // Delete selected element
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        e.preventDefault();
        removeElement(selectedId);
      }
      // Copy selected element
      if (e.key === 'c' && (e.ctrlKey || e.metaKey) && selectedId) {
        e.preventDefault();
        const el = elements.find(el => el.id === selectedId);
        if (el) setCopiedElement({ ...el });
      }
      // Paste
      if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        pasteFromClipboard();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, elements, copiedElement, editingTextId]);

  // Close context menu on any click
  useEffect(() => {
    const close = () => setContextMenu(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, []);

  // Paste: try internal clipboard first, then system clipboard for images
  const pasteFromClipboard = async () => {
    if (copiedElement) {
      const newEl = {
        ...copiedElement,
        id: Date.now().toString(),
        x: copiedElement.x + 20,
        y: copiedElement.y + 20,
      };
      setElements(prev => [...prev, newEl]);
      setSelectedId(newEl.id);
      return;
    }
    // Try system clipboard for images
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imageType = item.types.find(t => t.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          const file = new File([blob], 'pasted-image.png', { type: imageType });
          handleFile(file);
          return;
        }
      }
    } catch { /* clipboard API not available or denied */ }
  };

  const handleContextMenu = (e, type, elementId = null) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, type, elementId });
    if (elementId) setSelectedId(elementId);
  };

  // Auto-set engrave color when material changes
  useEffect(() => {
    const bestColor = MATERIAL_ENGRAVE_MAP[material] || '#000000';
    setElements(prev => prev.map(el => {
      if (el.options.engraveColor === bestColor) return el;
      const updated = { ...el, options: { ...el.options, engraveColor: bestColor } };
      setTimeout(() => processElement(updated), 200);
      return updated;
    }));
  }, [material]);

  // -- Canvas Drag & Drop files (only for external file drags) --
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);
  const onDragOver = useCallback((e) => {
    e.preventDefault();
    // Only show overlay for external file drags, not internal element drags
    if (e.dataTransfer.types.includes('Files')) setDragActive(true);
  }, []);
  const onDragLeave = useCallback(() => setDragActive(false), []);

  // -- Canvas Transforms (Pan / Zoom) --
  const handleZoomIn = () => setZoom(z => Math.min(z + 0.25, 5));
  const handleZoomOut = () => setZoom(z => Math.max(z - 0.25, 0.25));
  const handleZoomReset = () => { setZoom(1); setPanOffset({ x: 0, y: 0 }); };

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.15 : 0.15;
    setZoom(z => Math.min(Math.max(z + delta, 0.25), 5));
  }, []);

  useEffect(() => {
    const card = previewCardRef.current;
    if (card) {
      card.addEventListener('wheel', handleWheel, { passive: false });
      return () => card.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  const handleCanvasMouseDown = useCallback((e) => {
    if (e.target.closest('.canvas-element')) return; // let element handle it
    setSelectedId(null);
    if (e.button === 0) {
      isPanning.current = true;
      panStart.current = { x: e.clientX - panOffset.x, y: e.clientY - panOffset.y };
    }
  }, [panOffset]);

  // Global Pointer Events for Drag/Resize/Rotate/Shape-Stretch
  useEffect(() => {
    const handlePointerMove = (e) => {
      if (isPanning.current) {
        setPanOffset({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y });
      } else if (isStretchingShape.current) {
        const s = isStretchingShape.current;
        const dx = (e.clientX - s.startX) / zoom;
        const dy = (e.clientY - s.startY) / zoom;
        const tmpl = TEMPLATES[itemType]?.[templateShape];
        if (!tmpl) return;
        if (s.side === 'left' || s.side === 'right') {
          const dir = s.side === 'right' ? 1 : -1;
          const newSX = Math.max(0.5, Math.min(2, s.startStretchX + (dir * dx * 2) / tmpl.width));
          setShapeStretchX(newSX);
        } else {
          const dir = s.side === 'bottom' ? 1 : -1;
          const newSY = Math.max(0.5, Math.min(2, s.startStretchY + (dir * dy * 2) / tmpl.height));
          setShapeStretchY(newSY);
        }
      } else if (isTransforming.current) {
        const t = isTransforming.current;
        const dx = (e.clientX - t.startX) / zoom;
        const dy = (e.clientY - t.startY) / zoom;

        setElements(prev => {
          const newEls = prev.map(el => {
            if (el.id !== t.id) return el;
            if (t.action === 'move') {
              let newX = t.startVal.x + dx;
              let newY = t.startVal.y + dy;
              // Snap to other element centers
              const guides = [];
              for (const other of prev) {
                if (other.id === el.id) continue;
                if (Math.abs(newX - other.x) < SNAP_THRESHOLD) { newX = other.x; guides.push({ axis: 'x', pos: other.x }); }
                if (Math.abs(newY - other.y) < SNAP_THRESHOLD) { newY = other.y; guides.push({ axis: 'y', pos: other.y }); }
              }
              setSnapGuides(guides);
              return { ...el, x: newX, y: newY };
            } else if (t.action === 'resize') {
              if (el.type === 'text') {
                // Free resize for text — independent X and Y, anchor opposite edge
                const dirX = t.corner.includes('l') ? -1 : 1;
                const dirY = t.corner.includes('t') ? -1 : 1;
                const newW = Math.max(40, t.startVal.w + dx * dirX);
                const newH = Math.max(20, t.startVal.h + dy * dirY);
                return { ...el, width: newW, height: newH, x: t.startVal.x + (newW - t.startVal.w) / 2 * dirX, y: t.startVal.y + (newH - t.startVal.h) / 2 * dirY };
              }
              const scale = 1 + (dx / t.startVal.w) * (t.corner.includes('l') ? -1 : 1);
              return { ...el, width: Math.max(20, t.startVal.w * scale), height: Math.max(20, t.startVal.h * scale) };
            } else if (t.action === 'resize-edge') {
              // Single-axis resize for edge handles
              if (t.edge === 'left' || t.edge === 'right') {
                const dir = t.edge === 'right' ? 1 : -1;
                const newW = Math.max(40, t.startVal.w + dx * dir);
                return { ...el, width: newW, x: t.startVal.x + (newW - t.startVal.w) / 2 * dir };
              } else {
                const dir = t.edge === 'bottom' ? 1 : -1;
                const newH = Math.max(20, t.startVal.h + dy * dir);
                return { ...el, height: newH, y: t.startVal.y + (newH - t.startVal.h) / 2 * dir };
              }
            } else if (t.action === 'rotate') {
              // Get the element's screen-space center via the preview card
              const card = previewCardRef.current;
              if (!card) return el;
              const cardRect = card.getBoundingClientRect();
              // Element center in screen coords
              const cx = cardRect.left + el.x * zoom;
              const cy = cardRect.top + el.y * zoom;
              // Angle from center to mouse
              let angle = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI) + 90;
              // Snap to 45° increments if within 8° threshold
              const snapAngle = Math.round(angle / 45) * 45;
              if (Math.abs(angle - snapAngle) < 8) angle = snapAngle;
              return { ...el, rotation: angle };
            }
            return el;
          });
          return newEls;
        });
      }
    };
    const handlePointerUp = () => {
      isPanning.current = false;
      isTransforming.current = null;
      isStretchingShape.current = null;
      setSnapGuides([]);
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    const handleVisChange = () => { if (document.hidden) handlePointerUp(); };
    document.addEventListener('visibilitychange', handleVisChange);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      document.removeEventListener('visibilitychange', handleVisChange);
    };
  }, [zoom, itemType, templateShape]);

  const startTransform = (e, id, action, corner = null) => {
    e.stopPropagation();
    e.preventDefault(); // Prevent browser from intercepting pointer for text selection/drag
    setSelectedId(id);
    const el = elements.find(e => e.id === id);
    if (!el) return;
    isTransforming.current = {
      id, action, corner,
      edge: action === 'resize-edge' ? corner : null,
      startX: e.clientX, startY: e.clientY,
      startVal: { x: el.x, y: el.y, w: el.width, h: el.height, rot: el.rotation }
    };
  };

  // -- Exports --
  const downloadSvg = async () => {
    if (elements.length === 0) return;
    
    // Embed fonts if any text elements exist
    const hasText = elements.some(el => el.type === 'text');
    const fontCSS = hasText ? await getEmbeddedFontCSS() : '';
    const fontStyle = fontCSS ? `<defs><style type="text/css">${fontCSS}</style></defs>` : '';

    // The canvas coordinate space = stretched dimensions
    let w = 500, h = 500;
    let sx = 1, sy = 1; // scale factors from native path coords to canvas coords

    const engraveLayers = elements.map(el => {
      if (el.type === 'text') {
        const anchor = el.textAlign === 'left' ? 'start' : el.textAlign === 'right' ? 'end' : 'middle';
        const tx = el.textAlign === 'left' ? el.x - el.width / 2 : el.textAlign === 'right' ? el.x + el.width / 2 : el.x;
        // Offset y by ~0.35em to simulate vertical centering (dominant-baseline not supported by laser software)
        const ty = el.y + el.fontSize * 0.35;
        return `<text x="${tx}" y="${ty}" text-anchor="${anchor}" font-family="'${el.fontFamily}', sans-serif" font-size="${el.fontSize}" font-weight="${el.fontWeight}" fill="${el.color}" ${el.textDecoration === 'underline' ? 'text-decoration="underline"' : ''} transform="rotate(${el.rotation}, ${el.x}, ${el.y})">${el.text.replace(/&/g, '&amp;').replace(/</g, '&lt;')}</text>`;
      }
      if (el.type === 'vframe' && el.svgContent) {
        // Extract inner content of the SVG and embed with transform
        const innerMatch = el.svgContent.match(/<svg[^>]*>([\s\S]*?)<\/svg>/);
        const vbMatch = el.svgContent.match(/viewBox="([^"]+)"/);
        if (innerMatch) {
          const inner = innerMatch[1];
          const vb = vbMatch ? vbMatch[1].split(' ').map(Number) : [0, 0, 400, 400];
          const scaleX = el.width / vb[2];
          const scaleY = el.height / vb[3];
          const transform = `translate(${el.x - el.width / 2}, ${el.y - el.height / 2}) rotate(${el.rotation}, ${el.width / 2}, ${el.height / 2}) scale(${scaleX}, ${scaleY})`;
          return `<g transform="${transform}">${inner}</g>`;
        }
        return '';
      }
      if (!el.result?.engraveSvg) return '';
      const content = el.result.engraveSvg.match(/<svg[^>]*>([\s\S]*?)<\/svg>/)?.[1] || '';
      const scaleX = el.width / el.result.width;
      const scaleY = el.height / el.result.height;
      const cx = el.result.width / 2;
      const cy = el.result.height / 2;
      const transform = `translate(${el.x}, ${el.y}) rotate(${el.rotation}) scale(${scaleX}, ${scaleY}) translate(${-cx}, ${-cy})`;
      return `<g transform="${transform}">${content}</g>`;
    }).join('\n');

    let cutlinePaths = '';
    if (customShape) {
      w = Math.round(customShape.width * shapeStretchX);
      h = Math.round(customShape.height * shapeStretchY);
      const svbParts = customShape.viewBox.split(' ').map(Number);
      const nsx = w / svbParts[2];
      const nsy = h / svbParts[3];
      cutlinePaths = `<g transform="scale(${nsx}, ${nsy})"><path d="${customShape.path}" fill="none" stroke="#FF0000" stroke-width="${2 / Math.max(nsx, nsy)}" /></g>`;
    } else if (itemType === 'sticker' && stickerShape !== 'none') {
      const st = TEMPLATES.sticker?.[stickerShape];
      if (st) {
        w = Math.round(st.width * shapeStretchX);
        h = Math.round(st.height * shapeStretchY);
        sx = shapeStretchX;
        sy = shapeStretchY;
        cutlinePaths = `<g transform="scale(${sx}, ${sy})"><path d="${st.path}" fill="none" stroke="#FF0000" stroke-width="${2 / Math.max(sx, sy)}" /></g>`;
      }
    } else if (itemType === 'sticker' && elements[0]?.result?.cutlineSvg) {
      w = elements[0].result.width;
      h = elements[0].result.height;
      cutlinePaths = elements[0].result.cutlineSvg.match(/<svg[^>]*>([\s\S]*?)<\/svg>/)?.[1] || '';
    } else if (itemType !== 'sticker') {
      const t = TEMPLATES[itemType]?.[templateShape];
      if (t) {
        w = Math.round(t.width * shapeStretchX);
        h = Math.round(t.height * shapeStretchY);
        sx = shapeStretchX;
        sy = shapeStretchY;
        cutlinePaths = `<g transform="scale(${sx}, ${sy})"><path d="${getTemplatePath(itemType, templateShape, linkWidth)}" fill="none" stroke="#FF0000" stroke-width="${2 / Math.max(sx, sy)}" /></g>`;
      }
    }

    // -- Union cutline with overflowing element bounding boxes --
    // Only applies when we have a shape-based cutline (customShape, stickerShape, or template)
    const hasShapeCutline = customShape || (itemType === 'sticker' && stickerShape !== 'none') || itemType !== 'sticker';
    if (hasShapeCutline && elements.length > 0) {
      let shapePathForUnion = null;
      let unionSx = 1, unionSy = 1;

      if (customShape) {
        shapePathForUnion = customShape.path;
        const svbParts = customShape.viewBox.split(' ').map(Number);
        unionSx = w / svbParts[2];
        unionSy = h / svbParts[3];
      } else if (itemType === 'sticker' && stickerShape !== 'none') {
        const st = TEMPLATES.sticker?.[stickerShape];
        if (st) {
          shapePathForUnion = st.path;
          unionSx = shapeStretchX;
          unionSy = shapeStretchY;
        }
      } else if (itemType !== 'sticker') {
        const t = TEMPLATES[itemType]?.[templateShape];
        if (t) {
          shapePathForUnion = t.pathFn ? t.pathFn(linkWidth) : t.path;
          unionSx = shapeStretchX;
          unionSy = shapeStretchY;
        }
      }

      if (shapePathForUnion) {
        const offsetPx = globalOffsetMm * 10; // Convert mm offset to viewBox-scale px (approx)
        const unionPath = computeUnionCutline(shapePathForUnion, elements, offsetPx, unionSx, unionSy);
        if (unionPath) {
          // Union was needed — elements overflow. Use the union path directly (already in canvas coords)
          cutlinePaths = `<path d="${unionPath}" fill="none" stroke="#FF0000" stroke-width="2" />`;
          // Update w/h to encompass the union bounding box
          const pathCoords = unionPath.match(/-?\d+\.?\d*/g)?.map(Number) || [];
          let maxX = w, maxY = h;
          for (let i = 0; i < pathCoords.length; i += 2) {
            if (pathCoords[i] > maxX) maxX = pathCoords[i];
            if (pathCoords[i + 1] > maxY) maxY = pathCoords[i + 1];
          }
          w = Math.ceil(maxX);
          h = Math.ceil(maxY);
        }
      }
    }

    // Physical output from user-driven dimension
    const phys = getPhysicalSize();
    const physW = phys.w;
    const physH = phys.h;

    // SVG uses inch units for physical accuracy in laser software
    const finalSvg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${physW}in" height="${physH}in">
  ${fontStyle}
  <g id="cut-layer">${cutlinePaths}</g>
  <g id="engrave-layer">${engraveLayers}</g>
</svg>`;

    const blob = new Blob([finalSvg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `laserforge_${itemType}.svg`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const downloadPreviewPng = async () => {
    if (elements.length === 0 && itemType === 'sticker') return;

    // Embed fonts if any text elements exist
    const hasText = elements.some(el => el.type === 'text');
    const fontCSS = hasText ? await getEmbeddedFontCSS() : '';

    let w = 500, h = 500;
    let sx = 1, sy = 1;
    let templateClipId = 'preview-clip-' + Date.now();
    
    const mat = MATERIALS.find(m => m.id === material);
    const gradColors = mat?.swatchBg.match(/linear-gradient\([^,]+, ([^,]+),/);
    const baseColor = gradColors ? gradColors[1] : '#5c3d2e';

    let bgLayer = '';

    if (customShape) {
      w = Math.round(customShape.width * shapeStretchX);
      h = Math.round(customShape.height * shapeStretchY);
      const svbParts = customShape.viewBox.split(' ').map(Number);
      const nsx = w / svbParts[2];
      const nsy = h / svbParts[3];
      bgLayer = `
        <defs>
          <clipPath id="${templateClipId}">
            <path d="${customShape.path}" transform="scale(${nsx}, ${nsy})" />
          </clipPath>
        </defs>
        <g clip-path="url(#${templateClipId})">
          <rect width="${w}" height="${h}" fill="${baseColor}" />
        </g>`;
    } else if (itemType === 'sticker' && stickerShape !== 'none') {
      const st = TEMPLATES.sticker?.[stickerShape];
      if (st) {
        w = Math.round(st.width * shapeStretchX);
        h = Math.round(st.height * shapeStretchY);
        sx = shapeStretchX;
        sy = shapeStretchY;
        bgLayer = `
          <defs>
            <clipPath id="${templateClipId}">
              <path d="${st.path}" transform="scale(${sx}, ${sy})" />
            </clipPath>
          </defs>
          <g clip-path="url(#${templateClipId})">
            <rect width="${w}" height="${h}" fill="${baseColor}" />
          </g>`;
      }
    } else if (itemType === 'sticker') {
      w = elements[0]?.result?.width || 500;
      h = elements[0]?.result?.height || 500;
      bgLayer = `<rect width="${w}" height="${h}" fill="${baseColor}" />`;
    } else {
      const t = TEMPLATES[itemType]?.[templateShape];
      if (t) {
        w = Math.round(t.width * shapeStretchX);
        h = Math.round(t.height * shapeStretchY);
        sx = shapeStretchX;
        sy = shapeStretchY;
        const path = getTemplatePath(itemType, templateShape, linkWidth);
        bgLayer = `
          <defs>
            <clipPath id="${templateClipId}">
              <path d="${path}" transform="scale(${sx}, ${sy})" />
            </clipPath>
          </defs>
          <g clip-path="url(#${templateClipId})">
            <rect width="${w}" height="${h}" fill="${baseColor}" />
          </g>`;
      }
    }

    const engraveLayers = elements.map(el => {
      if (el.type === 'text') {
        const anchor = el.textAlign === 'left' ? 'start' : el.textAlign === 'right' ? 'end' : 'middle';
        const tx = el.textAlign === 'left' ? el.x - el.width / 2 : el.textAlign === 'right' ? el.x + el.width / 2 : el.x;
        const ty = el.y + el.fontSize * 0.35;
        return `<text x="${tx}" y="${ty}" text-anchor="${anchor}" font-family="'${el.fontFamily}', sans-serif" font-size="${el.fontSize}" font-weight="${el.fontWeight}" fill="${el.color}" ${el.textDecoration === 'underline' ? 'text-decoration="underline"' : ''} transform="rotate(${el.rotation}, ${el.x}, ${el.y})">${el.text.replace(/&/g, '&amp;').replace(/</g, '&lt;')}</text>`;
      }
      if (el.type === 'vframe' && el.svgContent) {
        const innerMatch = el.svgContent.match(/<svg[^>]*>([\s\S]*?)<\/svg>/);
        const vbMatch = el.svgContent.match(/viewBox="([^"]+)"/);
        if (innerMatch) {
          const inner = innerMatch[1];
          const vb = vbMatch ? vbMatch[1].split(' ').map(Number) : [0, 0, 400, 400];
          const scaleX = el.width / vb[2];
          const scaleY = el.height / vb[3];
          const transform = `translate(${el.x - el.width / 2}, ${el.y - el.height / 2}) rotate(${el.rotation}, ${el.width / 2}, ${el.height / 2}) scale(${scaleX}, ${scaleY})`;
          return `<g transform="${transform}">${inner}</g>`;
        }
        return '';
      }
      if (!el.result?.engraveSvg) return '';
      const content = el.result.engraveSvg.match(/<svg[^>]*>([\s\S]*?)<\/svg>/)?.[1] || '';
      const scaleX = el.width / (el.result.width || 1);
      const scaleY = el.height / (el.result.height || 1);
      const cx = (el.result.width || 1) / 2;
      const cy = (el.result.height || 1) / 2;
      const transform = `translate(${el.x}, ${el.y}) rotate(${el.rotation}) scale(${scaleX}, ${scaleY}) translate(${-cx}, ${-cy})`;
      return `<g transform="${transform}">${content}</g>`;
    }).join('\n');

    // Physical output from user-driven dimension
    const phys = getPhysicalSize();
    const physW = phys.w;
    const physH = phys.h;
    const pngW = Math.round(physW * exportDpi);
    const pngH = Math.round(physH * exportDpi);

    const fontStyle = fontCSS ? `<defs><style type="text/css">${fontCSS}</style></defs>` : '';

    const previewSvg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${pngW}" height="${pngH}">
  ${fontStyle}
  ${bgLayer}
  <g id="engrave-layer">${engraveLayers}</g>
</svg>`;

    const blob = new Blob([previewSvg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = pngW;
      canvas.height = pngH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, pngW, pngH);
      URL.revokeObjectURL(url);
      
      canvas.toBlob((pngBlob) => {
        if (!pngBlob) return;
        const pngUrl = URL.createObjectURL(pngBlob);
        const a = document.createElement('a');
        a.href = pngUrl;
        a.download = `laserforge_preview_${itemType}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(pngUrl), 100);
      }, 'image/png');
    };
    img.src = url;
  };

  const selectedEl = elements.find(e => e.id === selectedId);

  return (
    <>
      <header className="app-header">
        <div className="app-logo">
          <div className="app-logo-icon">LF</div>
          <div><h1>LaserForge</h1><span>Multi-Item Composer</span></div>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary btn-sm" onClick={() => { setShowUnitCalc(true); setUnitMm(''); setUnitInches(''); }}>📏 Unit Calculator</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowPresetModal(true)}>📂 Open Preset</button>
          {elements.length > 0 && (
            <>
              <button className="btn btn-secondary btn-sm" onClick={saveAsPreset} disabled={presetSaving}>{presetSaving ? '⏳ Saving…' : '💾 Save as Preset'}</button>
              <button className="btn btn-secondary btn-sm" onClick={downloadPreviewPng}>📸 Preview PNG</button>
              <button className="btn btn-primary btn-sm" onClick={downloadSvg}>⬇ Production SVG</button>
            </>
          )}
        </div>
      </header>

      <div className="app-layout">
        <aside className="sidebar">
          {/* Item Type */}
          <div className="sidebar-section">
            <div className="sidebar-section-title">Item Type</div>
            <div className="control-group">
              <select value={itemType} onChange={(e) => { setItemType(e.target.value); setElements([]); setShapeStretchX(1); setShapeStretchY(1); setEditingShape(false); }}>
                {ITEM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          {/* Sticker / Patch Shape */}
          {itemType === 'sticker' && (
            <div className="sidebar-section">
              <div className="sidebar-section-title">Patch Shape</div>
              <div className="shape-grid">
                {Object.entries(TEMPLATES.sticker || {}).map(([key, tmpl]) => (
                  <div
                    key={key}
                    className={`shape-option ${stickerShape === key ? 'selected' : ''}`}
                    onClick={() => { setStickerShape(key); setShapeStretchX(1); setShapeStretchY(1); setEditingShape(false); }}
                    title={tmpl.name}
                  >
                    <div className="shape-preview">
                      <svg viewBox={`0 0 ${tmpl.width} ${tmpl.height}`} preserveAspectRatio="xMidYMid meet">
                        <path d={tmpl.path} fill="var(--text-muted)" fillRule="evenodd" opacity="0.6" />
                      </svg>
                    </div>
                    <span className="shape-label">{tmpl.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* US State Border */}
          <div className="sidebar-section">
            <div className="sidebar-section-title">US State Border</div>
            <div className="control-group">
              <input
                type="text"
                placeholder="Search states..."
                value={stateSearch}
                onChange={(e) => setStateSearch(e.target.value)}
                style={{ width: '100%', marginBottom: 4 }}
              />
              <div className="state-grid" style={{ maxHeight: 150, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                {statesList
                  .filter(s => s.toLowerCase().includes(stateSearch.toLowerCase()))
                  .map(s => (
                    <button
                      key={s}
                      className={`btn btn-xs ${selectedState === s && customShape?.source === 'state' ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ fontSize: 9, padding: '3px 2px', textAlign: 'center', lineHeight: 1.1 }}
                      onClick={() => { if (selectedState === s) { setSelectedState(''); setCustomShape(null); } else loadStateShape(s); }}
                      title={s}
                    >
                      {s.length > 8 ? s.substring(0, 7) + '…' : s}
                    </button>
                  ))}
              </div>
              {customShape?.source === 'state' && (
                <button className="btn btn-danger btn-xs btn-block" style={{ marginTop: 4 }} onClick={() => { setSelectedState(''); setCustomShape(null); }}>✕ Clear State</button>
              )}
            </div>
          </div>

          {/* Decorative Vframe Borders */}
          <div className="sidebar-section">
            <div className="sidebar-section-title">Decorative Frames</div>
            <p style={{ fontSize: 10, color: 'var(--text-muted)', margin: '0 0 6px' }}>Click to add to canvas</p>
            {vframeIndex && (
              <>
                <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
                  {Object.entries(vframeIndex).map(([packId, packData]) => (
                    <button
                      key={packId}
                      className={`btn btn-xs ${vframePack === packId ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setVframePack(packId)}
                      style={{ flex: 1 }}
                    >
                      {packData.label}
                    </button>
                  ))}
                </div>
                <div className="vframe-grid" style={{ maxHeight: 200, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                  {(vframeIndex[vframePack]?.frames || []).map(frame => (
                    <div
                      key={frame.id}
                      className="shape-option"
                      style={{ padding: 3, cursor: 'pointer' }}
                      onClick={() => addVframeElement(frame)}
                      title={`Add Frame ${frame.id}`}
                    >
                      <div className="shape-preview" style={{ height: 40, pointerEvents: 'none' }} dangerouslySetInnerHTML={{ __html: frame.svgContent.replace(/<svg/, '<svg preserveAspectRatio="xMidYMid meet" style="width:100%;height:100%"') }} />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Template Shape Visual Grid (keychain / bookmark) */}
          {itemType !== 'sticker' && (
            <div className="sidebar-section">
              <div className="sidebar-section-title">Template Shape</div>
              <div className="shape-grid">
                {Object.entries(TEMPLATES[itemType] || {}).map(([key, tmpl]) => (
                  <div
                    key={key}
                    className={`shape-option ${templateShape === key ? 'selected' : ''}`}
                    onClick={() => { setTemplateShape(key); setShapeStretchX(1); setShapeStretchY(1); setEditingShape(false); }}
                    title={tmpl.name}
                  >
                    <div className="shape-preview">
                      <svg viewBox={`0 0 ${tmpl.width} ${tmpl.height}`} preserveAspectRatio="xMidYMid meet">
                        <path d={tmpl.pathFn ? (tmpl.hole ? tmpl.pathFn(templateShape === key ? linkWidth : tmpl.defaultLinkWidth) + ' ' + tmpl.hole : tmpl.pathFn(templateShape === key ? linkWidth : tmpl.defaultLinkWidth)) : (tmpl.hole ? tmpl.path + ' ' + tmpl.hole : tmpl.path)} fill="var(--text-muted)" fillRule="evenodd" opacity="0.6" />
                      </svg>
                    </div>
                    <span className="shape-label">{tmpl.name}</span>
                  </div>
                ))}
              </div>

              {/* Link Width slider for keychains */}
              {itemType === 'keychain' && (
                <div className="control-group" style={{marginTop: 8}}>
                  <label className="control-label">Link Width <span className="control-value">{linkWidth}px</span></label>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input type="range" min={20} max={200} step={2} value={linkWidth} onChange={(e) => setLinkWidth(Number(e.target.value))} style={{ flex: 1 }} />
                    <input type="number" min={10} max={250} value={linkWidth} onChange={(e) => setLinkWidth(Number(e.target.value))} style={{ width: 56 }} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Material */}
          <div className="sidebar-section">
            <div className="sidebar-section-title">Material</div>
            <div className="material-grid">
              {MATERIALS.map(m => (
                <div key={m.id} className={`material-option ${material === m.id ? 'selected' : ''}`} onClick={() => setMaterial(m.id)}>
                  <div className="material-swatch" style={{ background: m.swatchBg }} />
                </div>
              ))}
            </div>
            <button className="btn btn-secondary btn-sm btn-block" style={{ marginTop: 8 }} onClick={addTextElement}>✏️ Add Text</button>
          </div>

          {/* Physical Size */}
          <div className="sidebar-section">
            <div className="sidebar-section-title">Physical Size</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div className="control-group" style={{ flex: 1 }}>
                <label className="control-label">Width (in){sizeDriver === 'w' ? ' ●' : ''}</label>
                <input type="number" min="0.5" max="24" step="0.25"
                  value={sizeDriver === 'w' ? outputWidthInches : getPhysicalSize().w}
                  onChange={(e) => { setSizeDriver('w'); setOutputWidthInches(Math.max(0.5, Math.min(24, Number(e.target.value)))); }}
                  style={{ width: '100%', opacity: sizeDriver === 'w' ? 1 : 0.6 }} />
              </div>
              <div className="control-group" style={{ flex: 1 }}>
                <label className="control-label">Height (in){sizeDriver === 'h' ? ' ●' : ''}</label>
                <input type="number" min="0.5" max="24" step="0.25"
                  value={sizeDriver === 'h' ? outputHeightInches : getPhysicalSize().h}
                  onChange={(e) => { setSizeDriver('h'); setOutputHeightInches(Math.max(0.5, Math.min(24, Number(e.target.value)))); }}
                  style={{ width: '100%', opacity: sizeDriver === 'h' ? 1 : 0.6 }} />
              </div>
            </div>
            <p style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>PNG exports at 300 DPI · ● = driving dimension</p>
          </div>

          {/* Selected Image Settings (text/vframe controls are in the canvas toolbar) */}
          {selectedEl && selectedEl.type !== 'text' && selectedEl.type !== 'vframe' ? (
              <div className="sidebar-section">
                <div className="sidebar-section-title">Image Settings</div>
                <div className="toggle-row">
                  <span className="toggle-label">Remove Background</span>
                  <label className="toggle-switch">
                    <input type="checkbox" checked={selectedEl.options?.removeBackground} onChange={(e) => updateElementOption(selectedEl.id, 'removeBackground', e.target.checked)} />
                    <span className="toggle-slider" />
                  </label>
                </div>
                <div className="toggle-row">
                  <span className="toggle-label">Invert Colors</span>
                  <label className="toggle-switch">
                    <input type="checkbox" checked={selectedEl.options?.invertColors} onChange={(e) => updateElementOption(selectedEl.id, 'invertColors', e.target.checked)} />
                    <span className="toggle-slider" />
                  </label>
                </div>
                <div className="control-group">
                  <label className="control-label">Threshold <span className="control-value">{selectedEl.options?.threshold}</span></label>
                  <input type="range" min="0" max="255" value={selectedEl.options?.threshold || 128} onChange={(e) => updateElementOption(selectedEl.id, 'threshold', Number(e.target.value))} />
                </div>
                <div className="control-group">
                  <label className="control-label">Engrave Color</label>
                  <div className="engrave-color-grid">
                    {ENGRAVE_COLORS.map(c => (
                      <div key={c.value} className={`engrave-color-option ${selectedEl.options?.engraveColor === c.value ? 'selected' : ''}`} onClick={() => updateElementOption(selectedEl.id, 'engraveColor', c.value)}>
                        <div className="engrave-color-swatch" style={{ background: c.swatch }} />
                      </div>
                    ))}
                  </div>
                </div>
                <button className="btn btn-danger btn-sm btn-block" style={{marginTop: 8}} onClick={() => removeElement(selectedEl.id)}>🗑 Remove Image</button>
              </div>
          ) : !selectedEl ? (
            <div className="sidebar-section"><p style={{fontSize: 12, color: 'var(--text-muted)'}}>Drop an image on the canvas or select an existing one to edit.</p></div>
          ) : null}

          {itemType === 'sticker' && (
            <div className="sidebar-section">
              <div className="sidebar-section-title">Sticker Cutline</div>
              <div className="control-group">
                <label className="control-label">Offset <span className="control-value">{globalOffsetMm}mm</span></label>
                <input type="range" min="0" max="10" step="0.5" value={globalOffsetMm} onChange={(e) => setGlobalOffsetMm(Number(e.target.value))} />
              </div>
              <div className="control-group">
                <label className="control-label">Type</label>
                <select value={globalCutlineType} onChange={(e) => setGlobalCutlineType(e.target.value)}>
                  {CUTLINE_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Hidden file input for canvas click */}
          <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} style={{ display: 'none' }} />
        </aside>

        <main className="main-area">
          <div
            className={`canvas-container ${dragActive ? 'canvas-drop-active' : ''}`}
            onMouseDown={handleCanvasMouseDown}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onContextMenu={(e) => handleContextMenu(e, 'canvas')}
          >
            <div className="canvas-bg" />
            {dragActive && (
              <div className="canvas-drop-overlay">
                <div className="canvas-drop-message">📂 Drop image to add to canvas</div>
              </div>
            )}
            <div className="canvas-toolbar" onMouseDown={(e) => e.stopPropagation()}>
              <div className="canvas-toolbar-left">
                {/* Text element inline controls */}
                {selectedEl?.type === 'text' && (
                  <div className="inline-controls">
                    <select value={selectedEl.fontFamily} onChange={(e) => updateTextProp(selectedEl.id, 'fontFamily', e.target.value)}>
                      {TEXT_FONTS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                    <input type="number" min={6} max={400} value={selectedEl.fontSize} onChange={(e) => updateTextProp(selectedEl.id, 'fontSize', Number(e.target.value))} title="Font size" />
                    <input type="color" value={selectedEl.color} onChange={(e) => updateTextProp(selectedEl.id, 'color', e.target.value)} title="Text color" />
                    <div className="divider" />
                    <button className={`btn-icon ${selectedEl.fontWeight === 'bold' ? 'active' : ''}`} onClick={() => updateTextProp(selectedEl.id, 'fontWeight', selectedEl.fontWeight === 'bold' ? 'normal' : 'bold')} title="Bold" style={{fontWeight:'bold'}}>B</button>
                    <button className={`btn-icon ${selectedEl.textDecoration === 'underline' ? 'active' : ''}`} onClick={() => updateTextProp(selectedEl.id, 'textDecoration', selectedEl.textDecoration === 'underline' ? 'none' : 'underline')} title="Underline" style={{textDecoration:'underline'}}>U</button>
                    <div className="divider" />
                    <button className={`btn-icon ${selectedEl.textAlign === 'left' ? 'active' : ''}`} onClick={() => updateTextProp(selectedEl.id, 'textAlign', 'left')} title="Align left">⫷</button>
                    <button className={`btn-icon ${selectedEl.textAlign === 'center' ? 'active' : ''}`} onClick={() => updateTextProp(selectedEl.id, 'textAlign', 'center')} title="Align center">☰</button>
                    <button className={`btn-icon ${selectedEl.textAlign === 'right' ? 'active' : ''}`} onClick={() => updateTextProp(selectedEl.id, 'textAlign', 'right')} title="Align right">⫸</button>
                    <div className="divider" />
                    <button className="btn-icon" onClick={() => removeElement(selectedEl.id)} title="Delete" style={{color:'#ff4d4d'}}>🗑</button>
                  </div>
                )}
                {/* Vframe element inline controls */}
                {selectedEl?.type === 'vframe' && (
                  <div className="inline-controls">
                    <span className="inline-label">Frame {selectedEl.frameId}</span>
                    <div className="divider" />
                    <button className="btn-icon" onClick={() => removeElement(selectedEl.id)} title="Delete" style={{color:'#ff4d4d'}}>🗑</button>
                  </div>
                )}
                {/* Image element inline controls */}
                {selectedEl && selectedEl.type !== 'text' && selectedEl.type !== 'vframe' && (
                  <div className="inline-controls">
                    <span className="inline-label">Image</span>
                    <div className="divider" />
                    <button className="btn-icon" onClick={() => removeElement(selectedEl.id)} title="Delete" style={{color:'#ff4d4d'}}>🗑</button>
                  </div>
                )}
                {!selectedEl && elements.length === 0 && (
                  <button className="btn btn-secondary btn-sm" onClick={() => fileInputRef.current?.click()}>📂 Browse Files</button>
                )}
              </div>
              <div className="canvas-toolbar-right">
                <div className="zoom-controls">
                  <button className="zoom-btn" onClick={handleZoomIn}>+</button>
                  <span className="zoom-level">{Math.round(zoom * 100)}%</span>
                  <button className="zoom-btn" onClick={handleZoomOut}>−</button>
                  <button className="zoom-btn zoom-btn-reset" onClick={handleZoomReset}>⟳</button>
                </div>
              </div>
            </div>

            <div 
              className="preview-card" 
              ref={previewCardRef}
              style={{
                width: (() => {
                  if (customShape) return Math.round(customShape.width * shapeStretchX);
                  if (itemType === 'sticker') return stickerShape !== 'none' ? Math.round((TEMPLATES.sticker?.[stickerShape]?.width || 500) * shapeStretchX) : 500;
                  return Math.round((TEMPLATES[itemType]?.[templateShape]?.width || 500) * shapeStretchX);
                })(),
                height: (() => {
                  if (customShape) return Math.round(customShape.height * shapeStretchY);
                  if (itemType === 'sticker') return stickerShape !== 'none' ? Math.round((TEMPLATES.sticker?.[stickerShape]?.height || 500) * shapeStretchY) : 500;
                  return Math.round((TEMPLATES[itemType]?.[templateShape]?.height || 500) * shapeStretchY);
                })(),
                transform: `scale(${zoom}) translate(${panOffset.x / zoom}px, ${panOffset.y / zoom}px)`,
                cursor: zoom > 1 ? 'grab' : 'default',
                background: 'transparent',
                boxShadow: 'none',
                border: 'none',
                overflow: 'visible'
              }}
            >
              {/* Template Background Layer */}
              {customShape ? (
                /* Custom shape (state / vframe) background */
                <div style={{ position: 'absolute', inset: 0, filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.5))' }}>
                  {(() => {
                    const cw = Math.round(customShape.width * shapeStretchX);
                    const ch = Math.round(customShape.height * shapeStretchY);
                    const matColor = MATERIALS.find(m => m.id === material)?.swatchBg.match(/linear-gradient\([^,]+, ([^,]+),/)?.[1] || '#5c3d2e';
                    return (
                      <svg width="100%" height="100%" viewBox={customShape.viewBox} preserveAspectRatio="none">
                        <defs>
                          <clipPath id="custom-clip">
                            <path d={customShape.path} />
                          </clipPath>
                        </defs>
                        <g clipPath="url(#custom-clip)">
                          <rect width={customShape.width} height={customShape.height} fill={matColor} />
                          <foreignObject width={customShape.width} height={customShape.height}>
                            <div className={`material-texture ${MATERIALS.find(m => m.id === material)?.className || ''}`} style={{width:'100%', height:'100%'}} />
                          </foreignObject>
                        </g>
                        <path d={customShape.path} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="2" />
                        <path d={customShape.path} fill="none" stroke="#FF0000" strokeWidth="1" strokeDasharray="4,4" opacity="0.5" />
                      </svg>
                    );
                  })()}
                </div>
              ) : itemType !== 'sticker' ? (
                <div style={{ position: 'absolute', inset: 0, filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.5))' }}>
                  {(() => {
                    const si = getScaledTemplateInfo(itemType, templateShape, shapeStretchX, shapeStretchY, linkWidth);
                    if (!si) return null;
                    const matColor = MATERIALS.find(m => m.id === material)?.swatchBg.match(/linear-gradient\([^,]+, ([^,]+),/)?.[1] || '#5c3d2e';
                    return (
                      <svg width="100%" height="100%" viewBox={si.viewBox} preserveAspectRatio="none">
                        <defs>
                          <clipPath id="template-clip">
                            <path d={si.path} />
                          </clipPath>
                        </defs>
                        <g clipPath="url(#template-clip)">
                          <rect width={si.nativeWidth} height={si.nativeHeight} fill={matColor} />
                          <foreignObject width={si.nativeWidth} height={si.nativeHeight}>
                            <div className={`material-texture ${MATERIALS.find(m => m.id === material)?.className || ''}`} style={{width:'100%', height:'100%'}} />
                          </foreignObject>
                        </g>
                        <path d={si.path} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="2" />
                        <path d={si.path} fill="none" stroke="#FF0000" strokeWidth="1" strokeDasharray="4,4" opacity="0.5" />
                      </svg>
                    );
                  })()}
                </div>
              ) : stickerShape !== 'none' ? (
                <div style={{ position: 'absolute', inset: 0, filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.5))' }}>
                  {(() => {
                    const si = getScaledTemplateInfo('sticker', stickerShape, shapeStretchX, shapeStretchY);
                    if (!si) return null;
                    const matColor = MATERIALS.find(m => m.id === material)?.swatchBg.match(/linear-gradient\([^,]+, ([^,]+),/)?.[1] || '#5c3d2e';
                    return (
                      <svg width="100%" height="100%" viewBox={si.viewBox} preserveAspectRatio="none">
                        <defs>
                          <clipPath id="sticker-clip">
                            <path d={si.path} />
                          </clipPath>
                        </defs>
                        <g clipPath="url(#sticker-clip)">
                          <rect width={si.nativeWidth} height={si.nativeHeight} fill={matColor} />
                          <foreignObject width={si.nativeWidth} height={si.nativeHeight}>
                            <div className={`material-texture ${MATERIALS.find(m => m.id === material)?.className || ''}`} style={{width:'100%', height:'100%'}} />
                          </foreignObject>
                        </g>
                        <path d={si.path} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="2" />
                        <path d={si.path} fill="none" stroke="#FF0000" strokeWidth="1" strokeDasharray="4,4" opacity="0.5" />
                      </svg>
                    );
                  })()}
                </div>
              ) : (
                <div style={{ position: 'absolute', inset: 0, borderRadius: 20, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className={`material-texture ${MATERIALS.find(m => m.id === material)?.className || ''}`} style={{width:'100%', height:'100%'}} />
                </div>
              )}

              {/* Empty canvas hint */}
              {elements.length === 0 && (
                <div className="preview-placeholder">
                  <div className="preview-placeholder-icon">📂</div>
                  <h3>Drop an image or add text</h3>
                  <p>Drag and drop or <button onClick={() => fileInputRef.current?.click()} style={{background:'none',border:'none',color:'var(--accent-primary)',cursor:'pointer',textDecoration:'underline',font:'inherit'}}>browse files</button></p>
                </div>
              )}

              {/* Snap Guide Lines */}
              {snapGuides.map((g, i) => (
                g.axis === 'x'
                  ? <div key={`sg-${i}`} className="snap-guide snap-guide-x" style={{ left: g.pos, top: 0, height: '100%' }} />
                  : <div key={`sg-${i}`} className="snap-guide snap-guide-y" style={{ top: g.pos, left: 0, width: '100%' }} />
              ))}

              {/* Elements Layer */}
              {elements.map(el => {
                const isSelected = selectedId === el.id;
                const isRotating = isSelected && isTransforming.current?.action === 'rotate' && isTransforming.current?.id === el.id;
                const isTextEl = el.type === 'text';
                return (
                <div
                  key={el.id}
                  className={`canvas-element ${isSelected ? 'selected' : ''} ${isTextEl ? 'canvas-text-element' : ''}`}
                  style={{
                    position: 'absolute',
                    left: el.x,
                    top: el.y,
                    width: el.width,
                    height: el.height,
                    minHeight: isTextEl ? 20 : undefined,
                    transform: `translate(-50%, -50%) rotate(${el.rotation}deg)`,
                    pointerEvents: 'auto',
                    border: isSelected ? '2px solid var(--accent-primary)' : '2px solid transparent',
                    opacity: el.processing ? 0.5 : 1
                  }}
                  onMouseDown={(e) => { if (editingTextId === el.id) return; startTransform(e, el.id, 'move'); }}
                  onDoubleClick={() => { if (isTextEl && isSelected) setEditingTextId(el.id); }}
                  onContextMenu={(e) => handleContextMenu(e, 'element', el.id)}
                >
                  {/* Image element */}
                  {!isTextEl && el.type !== 'vframe' && el.result?.engraveSvg && (
                    <div style={{width: '100%', height: '100%'}} dangerouslySetInnerHTML={{__html: el.result.engraveSvg}} />
                  )}
                  {!isTextEl && el.type !== 'vframe' && itemType === 'sticker' && el.result?.cutlineSvg && (
                    <div style={{position: 'absolute', inset: 0, pointerEvents: 'none'}} dangerouslySetInnerHTML={{__html: el.result.cutlineSvg}} />
                  )}

                  {/* Vframe element */}
                  {el.type === 'vframe' && el.svgContent && (
                    <div style={{width: '100%', height: '100%', pointerEvents: 'none'}} dangerouslySetInnerHTML={{__html: el.svgContent.replace(/<svg/, '<svg preserveAspectRatio="none" style="width:100%;height:100%"')}} />
                  )}

                  {/* Text element */}
                  {isTextEl && (
                    <div
                      className="text-element-content"
                      style={{
                        fontFamily: el.fontFamily,
                        fontSize: el.fontSize,
                        fontWeight: el.fontWeight,
                        textDecoration: el.textDecoration,
                        textAlign: 'center',
                        color: el.color,
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.2,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        userSelect: editingTextId === el.id ? 'text' : 'none',
                        cursor: editingTextId === el.id ? 'text' : 'move',
                        padding: '4px 8px',
                        boxSizing: 'border-box',
                        outline: editingTextId === el.id ? '1px dashed var(--accent-primary)' : 'none',
                        minWidth: 40,
                        overflow: 'hidden',
                        pointerEvents: editingTextId === el.id ? 'auto' : 'none',
                      }}
                      contentEditable={editingTextId === el.id}
                      suppressContentEditableWarning
                      onBlur={(e) => {
                        updateTextProp(el.id, 'text', e.target.innerText);
                        setEditingTextId(null);
                      }}
                      onKeyDown={(e) => { if (e.key === 'Escape') { e.target.blur(); } e.stopPropagation(); }}
                      onMouseDown={(e) => { if (editingTextId === el.id) e.stopPropagation(); }}
                    >
                      {el.text}
                    </div>
                  )}

                  {isSelected && (
                    <>
                      <div className="resize-handle top-left" onMouseDown={(e) => startTransform(e, el.id, 'resize', 'tl')} />
                      <div className="resize-handle top-right" onMouseDown={(e) => startTransform(e, el.id, 'resize', 'tr')} />
                      <div className="resize-handle bottom-left" onMouseDown={(e) => startTransform(e, el.id, 'resize', 'bl')} />
                      <div className="resize-handle bottom-right" onMouseDown={(e) => startTransform(e, el.id, 'resize', 'br')} />
                      {/* Edge handles for text elements */}
                      {isTextEl && (
                        <>
                          <div className="resize-edge-handle edge-top" onMouseDown={(e) => startTransform(e, el.id, 'resize-edge', 'top')} />
                          <div className="resize-edge-handle edge-bottom" onMouseDown={(e) => startTransform(e, el.id, 'resize-edge', 'bottom')} />
                          <div className="resize-edge-handle edge-left" onMouseDown={(e) => startTransform(e, el.id, 'resize-edge', 'left')} />
                          <div className="resize-edge-handle edge-right" onMouseDown={(e) => startTransform(e, el.id, 'resize-edge', 'right')} />
                        </>
                      )}
                      <div className="rotate-handle" onMouseDown={(e) => startTransform(e, el.id, 'rotate')} />
                      {/* Rotation spoke indicators - visible during rotate */}
                      {isRotating && (
                        <div className="rotation-spokes" style={{ position: 'absolute', inset: '-40px', pointerEvents: 'none' }}>
                          {[0,45,90,135,180,225,270,315].map(deg => {
                            const snapped = Math.abs(((el.rotation % 360) + 360) % 360 - deg) < 8;
                            return <div key={deg} className={`rotation-spoke ${snapped ? 'snapped' : ''}`} style={{ transform: `rotate(${deg}deg)` }} />;
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );})}

              {/* On-canvas Edit Shape controls */}
              {itemType !== 'sticker' && (
                <div className="canvas-edit-shape-controls" style={{ position: 'absolute', top: 8, right: 8, zIndex: 50, display: 'flex', gap: 4 }}>
                  <button className={`btn btn-xs ${editingShape ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setEditingShape(s => !s)}>
                    {editingShape ? '✓ Done' : '✏️ Edit Shape'}
                  </button>
                  {editingShape && (
                    <button className="btn btn-xs btn-secondary" onClick={() => { setShapeStretchX(1); setShapeStretchY(1); }}>↺ Reset</button>
                  )}
                </div>
              )}

              {/* Shape stretch drag handles (when editing shape) */}
              {editingShape && itemType !== 'sticker' && (
                <>
                  <div className="shape-stretch-handle shape-stretch-left" onMouseDown={(e) => { e.stopPropagation(); isStretchingShape.current = { side: 'left', startX: e.clientX, startY: e.clientY, startStretchX: shapeStretchX, startStretchY: shapeStretchY }; }} />
                  <div className="shape-stretch-handle shape-stretch-right" onMouseDown={(e) => { e.stopPropagation(); isStretchingShape.current = { side: 'right', startX: e.clientX, startY: e.clientY, startStretchX: shapeStretchX, startStretchY: shapeStretchY }; }} />
                  <div className="shape-stretch-handle shape-stretch-top" onMouseDown={(e) => { e.stopPropagation(); isStretchingShape.current = { side: 'top', startX: e.clientX, startY: e.clientY, startStretchX: shapeStretchX, startStretchY: shapeStretchY }; }} />
                  <div className="shape-stretch-handle shape-stretch-bottom" onMouseDown={(e) => { e.stopPropagation(); isStretchingShape.current = { side: 'bottom', startX: e.clientX, startY: e.clientY, startStretchX: shapeStretchX, startStretchY: shapeStretchY }; }} />
                  <div className="shape-stretch-border" />
                </>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div className="context-menu" style={{ left: contextMenu.x, top: contextMenu.y }}>
          {contextMenu.type === 'element' && (
            <>
              <button className="context-menu-item" onClick={() => {
                const el = elements.find(e => e.id === contextMenu.elementId);
                if (el) setCopiedElement({ ...el });
                setContextMenu(null);
              }}>📋 Copy</button>
              <button className="context-menu-item context-menu-danger" onClick={() => {
                if (contextMenu.elementId) removeElement(contextMenu.elementId);
                setContextMenu(null);
              }}>🗑 Delete</button>
            </>
          )}
          {contextMenu.type === 'canvas' && (
            <>
              <button className="context-menu-item" disabled={!copiedElement} onClick={() => {
                pasteFromClipboard();
                setContextMenu(null);
              }}>📋 Paste{!copiedElement ? ' (nothing copied)' : ''}</button>
              <button className="context-menu-item" onClick={() => {
                fileInputRef.current?.click();
                setContextMenu(null);
              }}>📂 Browse Files</button>
            </>
          )}
        </div>
      )}

      {/* Preset Browser Modal */}
      {showPresetModal && (
        <div className="preset-modal-overlay" onClick={() => { setShowPresetModal(false); setPresetDeleteConfirm(null); }}>
          <div className="preset-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preset-modal-header">
              <h2>📂 Design Presets</h2>
              <button className="preset-modal-close" onClick={() => { setShowPresetModal(false); setPresetDeleteConfirm(null); }}>✕</button>
            </div>
            <div className="preset-modal-search">
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  placeholder="Search presets…"
                  value={presetSearch}
                  onChange={(e) => setPresetSearch(e.target.value)}
                  autoFocus
                  style={{ flex: 1 }}
                />
                <select
                  value={presetCategory}
                  onChange={(e) => setPresetCategory(e.target.value)}
                  style={{ width: 140, padding: '8px 6px', background: 'var(--bg-input)', border: '1px solid var(--border-medium)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 12 }}
                >
                  <option value="all">All Categories</option>
                  {ITEM_TYPES.map(t => <option key={t.value} value={t.label}>{t.label}</option>)}
                </select>
              </div>
            </div>
            <div className="preset-modal-list">
              {(() => {
                const filtered = presets.filter(p => {
                  const matchesSearch = !presetSearch || p.name.toLowerCase().includes(presetSearch.toLowerCase());
                  const matchesCategory = presetCategory === 'all' || (p.category || '').toLowerCase() === presetCategory.toLowerCase();
                  return matchesSearch && matchesCategory;
                });
                return filtered.length === 0 ? (
                  <div className="preset-empty">
                    <p>No presets found{presetSearch ? ` for "${presetSearch}"` : ''}{presetCategory !== 'all' ? ` in ${presetCategory}` : ''}.</p>
                    <p style={{fontSize: 11, color: 'var(--text-muted)'}}>Use &quot;Save as Preset&quot; to save your current design.</p>
                  </div>
                ) : (
                  filtered.map(preset => (
                    <div key={preset.id} className="preset-item">
                      <div className="preset-item-info" onClick={() => loadPreset(preset)}>
                        <div className="preset-item-name">{preset.name}</div>
                        <div className="preset-item-meta">
                          {preset.category && <span className="preset-tag">{preset.category}</span>}
                          {preset.data?.elements && <span className="preset-tag" style={{background: 'var(--border-medium)'}}>{preset.data.elements.length} element{preset.data.elements.length !== 1 ? 's' : ''}</span>}
                          <span className="preset-date">{new Date(preset.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <button className="preset-delete-btn" onClick={(e) => { e.stopPropagation(); setPresetDeleteConfirm(preset.id); }} title="Delete preset">🗑</button>
                    </div>
                  ))
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {presetDeleteConfirm && (
        <div className="preset-modal-overlay" style={{zIndex: 1001}} onClick={() => setPresetDeleteConfirm(null)}>
          <div className="preset-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Preset?</h3>
            <p>This action cannot be undone.</p>
            <div className="preset-confirm-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => setPresetDeleteConfirm(null)}>Cancel</button>
              <button className="btn btn-danger btn-sm" onClick={() => deletePreset(presetDeleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Unit Calculator Modal */}
      {showUnitCalc && (
        <div className="preset-modal-overlay" onClick={() => setShowUnitCalc(false)}>
          <div className="preset-confirm-dialog" onClick={(e) => e.stopPropagation()} style={{width: 'min(340px, 85vw)'}}>
            <h3>📏 Unit Calculator</h3>
            <div style={{display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16}}>
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <label style={{width: 60, fontSize: 13, color: 'var(--text-secondary)', textAlign: 'right'}}>mm</label>
                <input
                  type="number"
                  step="any"
                  value={unitMm}
                  onChange={(e) => {
                    const v = e.target.value;
                    setUnitMm(v);
                    setUnitInches(v === '' ? '' : (parseFloat(v) / 25.4).toFixed(4));
                  }}
                  placeholder="0"
                  style={{flex: 1, padding: '8px 10px', background: 'var(--bg-input)', border: '1px solid var(--border-medium)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 14}}
                  autoFocus
                />
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <label style={{width: 60, fontSize: 13, color: 'var(--text-secondary)', textAlign: 'right'}}>inches</label>
                <input
                  type="number"
                  step="any"
                  value={unitInches}
                  onChange={(e) => {
                    const v = e.target.value;
                    setUnitInches(v);
                    setUnitMm(v === '' ? '' : (parseFloat(v) * 25.4).toFixed(4));
                  }}
                  placeholder="0"
                  style={{flex: 1, padding: '8px 10px', background: 'var(--bg-input)', border: '1px solid var(--border-medium)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 14}}
                />
              </div>
            </div>
            <div style={{marginTop: 16}}>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowUnitCalc(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
